// src/components/firstAider/FirstAiderRosterSheet.tsx — the "who are the first
// aiders" pop-up card, opened by tapping the registered/signed-in count tag on
// the compose form.
//
// Lists every registered first aider, signed-in first, each with a signed-in
// badge and the contact methods the system can reach them by (Email always;
// Text only when a verified mobile is on file). Fetches on open.

import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { C, F } from "../../theme";
import { FA, fetchRoster, type RosterEntry, type RosterResponse } from "../../lib/firstAider";
import { FaKeyframes, PulseDot } from "./kit";

export function FirstAiderRosterSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [data, setData] = useState<RosterResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setData(null);
    setError(null);
    let cancelled = false;
    fetchRoster()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load the first aider list.");
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="First aiders" onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div>
            <div style={eyebrowStyle}>First aiders</div>
            <div style={titleStyle}>
              {data
                ? `${data.registeredCount} registered · ${data.signedInCount} signed in`
                : "Who's available"}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={closeBtnStyle}>
            ✕
          </button>
        </div>

        <div style={bodyStyle}>
          {error && <div style={errorStyle}>{error}</div>}
          {!error && !data && <div style={loadingStyle}>Loading…</div>}
          {!error && data && data.firstAiders.length === 0 && (
            <div style={loadingStyle}>No first aiders registered yet.</div>
          )}
          {!error &&
            data &&
            data.firstAiders.map((fa) => <RosterRow key={fa.id} fa={fa} />)}
        </div>

        <div style={footnoteStyle}>
          Everyone registered receives the callout (in-app + email). “Signed in” means they’re
          on duty right now.
        </div>
      </div>
      <FaKeyframes />
    </div>
  );
}

function RosterRow({ fa }: { fa: RosterEntry }) {
  const name = `${fa.firstName ?? ""} ${fa.lastName ?? ""}`.trim() || "Unknown";
  return (
    <div style={rowStyle}>
      <span style={{ ...avatarStyle, background: fa.signedIn ? `linear-gradient(135deg, ${FA.go}, ${FA.goLt})` : `linear-gradient(135deg, ${FA.pending}, #C9B894)` }}>
        {fa.initials}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={nameStyle}>{name}</div>
        {fa.phone && (
          <a href={`tel:${fa.phone}`} style={phoneStyle}>{fa.phone}</a>
        )}
        <div style={methodsRowStyle}>
          {fa.hasEmail && <span style={methodChipStyle}>Email</span>}
          {fa.canText && <span style={methodChipStyle}>Text</span>}
          {!fa.hasEmail && !fa.canText && <span style={methodNoneStyle}>No contact on file</span>}
        </div>
      </div>
      {fa.signedIn ? (
        <span style={signedInBadgeStyle}>
          <PulseDot tone="red" />
          Signed in
        </span>
      ) : (
        <span style={registeredBadgeStyle}>Registered</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(43,32,20,0.5)",
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  zIndex: 320,
  padding: 0,
};

const cardStyle: CSSProperties = {
  background: C.card,
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  width: "100%",
  maxWidth: 480,
  maxHeight: "80vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 -10px 40px rgba(0,0,0,0.3)",
  paddingBottom: "env(safe-area-inset-bottom)",
};

const headerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "16px 18px 12px",
  borderBottom: `1px solid ${C.hairlineLt}`,
};

const eyebrowStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 9.5,
  fontWeight: 800,
  letterSpacing: 1,
  textTransform: "uppercase",
  color: FA.red,
};

const titleStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 18,
  fontWeight: 700,
  color: C.ink,
  letterSpacing: -0.2,
  marginTop: 2,
};

const closeBtnStyle: CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: 10,
  background: C.paperWarm,
  border: `1px solid ${C.hairline}`,
  color: C.inkSoft,
  fontSize: 15,
  fontWeight: 800,
  cursor: "pointer",
  flexShrink: 0,
  lineHeight: 1,
};

const bodyStyle: CSSProperties = {
  overflowY: "auto",
  padding: "4px 8px",
  flex: 1,
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "11px 10px",
  borderBottom: `1px solid ${C.hairlineLt}`,
};

const avatarStyle: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#FFFFFF",
  fontFamily: F.body,
  fontSize: 13,
  fontWeight: 800,
  flexShrink: 0,
  letterSpacing: 0.3,
};

const nameStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 16,
  fontWeight: 700,
  color: C.ink,
  letterSpacing: -0.2,
};

const phoneStyle: CSSProperties = {
  display: "inline-block",
  marginTop: 3,
  fontFamily: F.mono,
  fontSize: 13,
  fontWeight: 700,
  color: FA.redDk,
  letterSpacing: 0.4,
  textDecoration: "none",
};

const methodsRowStyle: CSSProperties = {
  display: "flex",
  gap: 6,
  marginTop: 4,
  flexWrap: "wrap",
};

const methodChipStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: 0.4,
  color: C.sageDk,
  background: C.sageBg,
  border: `1px solid ${C.sageLt}`,
  borderRadius: 999,
  padding: "2px 9px",
};

const methodNoneStyle: CSSProperties = {
  fontFamily: F.serif,
  fontStyle: "italic",
  fontSize: 12,
  color: C.inkMute,
};

const signedInBadgeStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "5px 11px",
  borderRadius: 999,
  background: FA.goBg,
  border: `1px solid ${FA.goLt}`,
  color: FA.go,
  fontFamily: F.body,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.3,
  flexShrink: 0,
};

const registeredBadgeStyle: CSSProperties = {
  display: "inline-block",
  padding: "5px 11px",
  borderRadius: 999,
  background: C.paperWarm,
  border: `1px solid ${C.hairline}`,
  color: C.inkSoft,
  fontFamily: F.body,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.3,
  flexShrink: 0,
};

const footnoteStyle: CSSProperties = {
  padding: "10px 18px 16px",
  borderTop: `1px solid ${C.hairlineLt}`,
  fontFamily: F.serif,
  fontStyle: "italic",
  fontSize: 12.5,
  color: C.inkSoft,
  lineHeight: 1.4,
};

const errorStyle: CSSProperties = {
  padding: "14px",
  fontFamily: F.body,
  fontWeight: 700,
  fontSize: 13,
  color: FA.redDk,
  textAlign: "center",
};

const loadingStyle: CSSProperties = {
  padding: "20px",
  fontFamily: F.serif,
  fontStyle: "italic",
  color: C.inkMute,
  textAlign: "center",
};
